import React, { useState, useMemo, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { Text, Button, Stack, Input } from 'rsuite';
import {
  PresentationCol,
  PresentationGrid,
  PresentationRow,
} from '../../layout/PresentationLayout';
import apiAccess from '../../../api/apiAccess';
import {
  STRIPE_NO_PAYMENT_METHOD_ATTACHED_INTENT_STATUSES,
  STRIPE_PAYMENT_INTENT_STATUSES,
} from './stripeConstants';
import {
  ACCOUNT_PICKER_CANCELLED_ALERT_MSG,
  ACCOUNT_PICKER_ERROR_ALERT_MSG,
  PAY_ACH_ERROR,
} from '../constants';
import { useSelector } from 'react-redux';
import { selectCurrentUserEmail } from '../../../store/slices/auth/authSelectors';
import {
  selectPaymentFees,
  selectPaymentID,
  selectPaymentSignatory,
  selectSelectedPaymentMethod,
} from '../../../store/slices/payment/paymentSelectors';
import Notice from '../../user-feedback/Notice';
import IconIndicator from '../../user-feedback/IconIndicator';
import { convertNumberToCurrency } from '../../../common/utils';
import { calculateTotalDue } from '../common/utils';

const StripeACHAccountHolderForm = ({
  stripe,
  setBankAccountDetails,
  setClientSecret,
}) => {
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountHolderEmail, setAccountHolderEmail] = useState('');
  const [paymentInProgress, setPaymentsInProgress] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const currentUserEmail = useSelector(selectCurrentUserEmail);
  const signatoryName = useSelector(selectPaymentSignatory);
  const paymentID = useSelector(selectPaymentID);

  const paymentFees = useSelector(selectPaymentFees);
  const selectedPaymentMethod = useSelector(selectSelectedPaymentMethod);

  const totalDue = useMemo(
    () => calculateTotalDue(selectedPaymentMethod, paymentFees),
    [selectedPaymentMethod, paymentFees]
  );

  useEffect(() => {
    setAccountHolderEmail(currentUserEmail);
  }, [currentUserEmail]);

  useEffect(() => {
    setAccountHolderName(signatoryName);
  }, [signatoryName]);

  const payButtonDisabled = useMemo(
    () => paymentInProgress || !stripe,
    [paymentInProgress, stripe]
  );

  const paymentClick = async () => {
    setErrorMessage('');
    setPaymentsInProgress(true);
    try {
      const { clientSecret } = await apiAccess.payByACHDirectDebit(
        paymentID,
        signatoryName,
        currentUserEmail
      );

      const { paymentIntent, error } =
        await stripe.collectBankAccountForPayment({
          clientSecret,
          params: {
            payment_method_type: 'us_bank_account',
            payment_method_data: {
              billing_details: {
                name: accountHolderName,
                email: accountHolderEmail.trim(),
              },
            },
          },
          expand: ['payment_method'],
        });

      if (
        !error &&
        paymentIntent.status ===
          STRIPE_PAYMENT_INTENT_STATUSES.REQUIRES_CONFIRMATION
      ) {
        const {
          account_number,
          routing_number,
          account_type,
          bank_name,
          last4,
        } = paymentIntent?.payment_method?.us_bank_account || {};

        const { name } = paymentIntent?.payment_method?.billing_details || {};

        setBankAccountDetails({
          accountNumber: account_number,
          routingNumber: routing_number,
          accountType: account_type,
          bankName: bank_name,
          accountHolder: name,
          last4,
        });
        setClientSecret(paymentIntent.client_secret);
      } else if (
        STRIPE_NO_PAYMENT_METHOD_ATTACHED_INTENT_STATUSES.includes(
          paymentIntent?.status
        )
      ) {
        setErrorMessage(ACCOUNT_PICKER_CANCELLED_ALERT_MSG);
      } else if (error) {
        setErrorMessage(ACCOUNT_PICKER_ERROR_ALERT_MSG);
      } else {
        setErrorMessage(ACCOUNT_PICKER_ERROR_ALERT_MSG);
      }
    } catch (err) {
      setErrorMessage(PAY_ACH_ERROR);
      Sentry.captureException(err);
    } finally {
      setPaymentsInProgress(false);
    }
  };
  return (
    <>
      <PresentationGrid fluid>
        <PresentationRow className="mb-16">
          <PresentationCol xs={24}>
            <Stack
              direction="column"
              spacing={16}
              alignItems="flex-start"
            >
              <Stack.Item>
                <Text className="text-small">
                  To make a secure payment, connect to your bank account and
                  verify your identity.
                </Text>
              </Stack.Item>
              <Stack.Item>
                <Text className="text-small">
                  Please ensure that the name provided matches the name on file
                  for the bank account and disable any active pop-up blockers as
                  they could interfere with the payment process.
                </Text>
              </Stack.Item>
            </Stack>
          </PresentationCol>
        </PresentationRow>
        <PresentationRow className="mb-16">
          <PresentationCol xs={24}>
            <Text
              id="label-ach-account-holder-name"
              className="text-small mb-4"
            >
              Account Holder&apos;s Full Name
            </Text>
            <Input
              id="ach-account-holder-name"
              aria-labelledby="label-ach-account-holder-name"
              autoComplete="name"
              value={accountHolderName}
              onChange={setAccountHolderName}
            />
          </PresentationCol>
        </PresentationRow>
        <PresentationRow className="mb-16">
          <PresentationCol xs={24}>
            <Text
              id="label-ach-account-holder-email"
              className="text-small mb-4"
            >
              Email
            </Text>
            <Input
              id="ach-account-holder-email"
              aria-labelledby="label-ach-account-holder-email"
              autoComplete="email"
              value={accountHolderEmail}
              onChange={setAccountHolderEmail}
            />
          </PresentationCol>
        </PresentationRow>
        {errorMessage && (
          <PresentationRow>
            <PresentationCol xs={24}>
              <Notice message={errorMessage} />
            </PresentationCol>
          </PresentationRow>
        )}
        <PresentationRow>
          <PresentationCol xs={24}>
            {paymentInProgress && <IconIndicator />}
            <Button
              className="btn-primary full-width"
              onClick={() => paymentClick()}
              disabled={payButtonDisabled}
            >
              Pay {convertNumberToCurrency(totalDue)}
            </Button>
          </PresentationCol>
        </PresentationRow>
      </PresentationGrid>
    </>
  );
};

export default StripeACHAccountHolderForm;
